import React, { useEffect, useId, useRef } from 'react';
import { RiArrowDownSLine, RiCheckLine } from 'react-icons/ri';

//! ----------------------------> SHARED FILTER DROPDOWN (expand-height)
//* The options panel is rendered in normal flow (not absolutely positioned),
//* so opening it grows the wrapper card and the filter bar itself – iOS style.
//* Open/close state is owned by the parent so only one dropdown is open at a time.
const FilterDropdown = ({
    icon: Icon,
    label,
    value,
    options,
    isOpen,
    onToggle,
    onClose,
    onSelect,
    className = '',
}) => {
    const rootRef = useRef(null);
    const listId = useId();

    //? Close on outside tap/click and on Escape while open
    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                onClose();
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    const selectedOption = options.find((option) => option.value === value);
    const buttonText = selectedOption ? selectedOption.label : label;

    const handleOptionKeyDown = (event, optionValue) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(optionValue);
        }
    };

    return (
        <div ref={rootRef} className={`dropdown ${isOpen ? 'open' : ''} ${className}`}>
            <div className="dropdown-wrapper">
                <button
                    type="button"
                    className="dropdown-btn"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    onClick={onToggle}
                >
                    <Icon className="dropdown-icon-primary" aria-hidden="true" />
                    <span className="search-text text-white font-medium">{buttonText}</span>
                    <span className="dropdown-chevron" aria-hidden="true">
                        <RiArrowDownSLine className="dropdown-icon-secondary text-white" />
                    </span>
                </button>

                {/* grid-template-rows 0fr -> 1fr animates the height to "auto" */}
                <div className="dropdown-panel" aria-hidden={!isOpen}>
                    <div className="dropdown-panel-inner">
                        <ul id={listId} role="listbox" aria-label={label} className="dropdown-menu">
                            {options.map((option, index) => {
                                const isSelected = option.value === value;
                                return (
                                    <li
                                        key={option.value}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={isOpen ? 0 : -1}
                                        style={{ '--i': index }}
                                        onClick={() => onSelect(option.value)}
                                        onKeyDown={(event) => handleOptionKeyDown(event, option.value)}
                                    >
                                        <span className="dropdown-option-label">{option.label}</span>
                                        <RiCheckLine className="dropdown-check" aria-hidden="true" />
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterDropdown;
